import React from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calcPriceExclVat, calcPriceInclVat, formatPriceWithCurrency } from '../../utils/vat';

/**
 * The grey line under a price field that carries a VatModeToggle: the same price
 * the other way round, so switching the toggle always visibly does something.
 *
 * Renders in BOTH states. The toggle never moves the typed number, so this line
 * is the only feedback that the click landed.
 *
 * `vatRate` is required on purpose: margin-VAT and insurance lines must not
 * silently fall back to the standard rate. calcPrice*Vat already round
 * half-away-from-zero, matching the backend's round_money, negatives included.
 *
 * `suffix` appends a unit to the converse price, e.g. "/h" on an hourly rate.
 */
function VatConverseLine({ value, mode, vatRate, suffix, sx, ...rest }) {
  const { t } = useTranslation();

  if (value === null || value === undefined || value === '') return null;
  const numeric = parseFloat(value);
  if (Number.isNaN(numeric)) return null;

  const converse = mode === 'incl'
    ? `= ${formatPriceWithCurrency(calcPriceExclVat(numeric, vatRate))} ${t('excl. VAT')}`
    : `= ${formatPriceWithCurrency(calcPriceInclVat(numeric, vatRate))} ${t('incl. VAT')}`;

  return (
    <Typography
      data-testid="vat-converse-line"
      sx={{ fontSize: '12px', color: 'text.secondary', ...sx }}
      {...rest}
    >
      {converse}{suffix}
    </Typography>
  );
}

export default VatConverseLine;
